import React, { useState, useEffect, useRef, useMemo } from 'react';
import { log, logError, logWarn, downloadLogs, flushLogs } from './logger';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { generateFanCurve, generateCurveForVoltage, getRPMForVoltage } from './utils/fanLaws';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

/**
 * Calculate absorption power using the formula: airflow × power / efficiency
 * 
 * @param {number} airflow - Volume flow rate in m³/h (qv value)
 * @param {number} power - Electrical input power in watts (W)
 * @param {number} efficiency - Fan efficiency (0-1 for decimal, or 0-100 for percentage)
 * @param {boolean} efficiencyIsPercentage - If true, efficiency is treated as percentage (0-100), otherwise as decimal (0-1)
 * @returns {number} Absorption power in watts (W)
 */
// eslint-disable-next-line no-unused-vars
function calculateAbsorptionPower(airflow, power, efficiency, efficiencyIsPercentage = false) {
  if (!airflow || !power || !efficiency || efficiency === 0) {
    return 0;
  }
  
  const efficiencyDecimal = efficiencyIsPercentage ? efficiency / 100 : efficiency;
  const absorptionPower = (airflow * power) / efficiencyDecimal;
  
  return absorptionPower;
}

function FanGraphChartJS() {
  const chartRef = useRef(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operatingPoint, setOperatingPoint] = useState({ qv: 2335.46, psf: 762.77 });
  const [showAdditionalCurves, setShowAdditionalCurves] = useState({
    current: false,
    fanPressure: false,
    power: false,
    staticEfficiency: false,
    totalEfficiency: false,
    speed: false
  });
  
  // FIXED DOMAIN: Calculate once when data loads, never change
  const [fixedDomain, setFixedDomain] = useState(null);
  const clickHistoryRef = useRef([]);
  
  // Motor options state
  const [motorOptions, setMotorOptions] = useState([]);
  const [selectedMotor, setSelectedMotor] = useState(null);
  
  // Catalog specifications state
  const [catalogSpecs, setCatalogSpecs] = useState(null);
  
  // Fan family selection state
  const [selectedFanFamily, setSelectedFanFamily] = useState(null);
  const [availableFanFamilies, setAvailableFanFamilies] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  
  // Store all families data
  const [allFamiliesData, setAllFamiliesData] = useState(null);

  // Log operating point changes
  useEffect(() => {
    log('FanGraphChartJS', 'Operating point changed', operatingPoint);
  }, [operatingPoint]);

  // Log selected motor changes and force chart update (without animation)
  useEffect(() => {
    log('FanGraphChartJS', 'Selected motor changed', {
      selectedMotor: selectedMotor ? {
        power: selectedMotor.power_rating_kw,
        rpm: selectedMotor.fan_rpm
      } : null
    });
    
    // Force chart update if chart exists and is fully initialized (without animation)
    if (chartRef.current && chartRef.current.canvas && chartRef.current.canvas.ownerDocument) {
      const chart = chartRef.current;
      try {
        chart.update('none'); // Update without animation
        log('FanGraphChartJS', 'Chart updated after motor selection change');
      } catch (error) {
        logWarn('FanGraphChartJS', 'Error updating chart after motor selection change', error);
      }
    }
  }, [selectedMotor]);
  
  // Force chart update when graphData changes (e.g., when fan family changes)
  useEffect(() => {
    if (chartRef.current && graphData) {
      const chart = chartRef.current;
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        // Check if chart is fully initialized before updating
        if (chart.canvas && chart.canvas.ownerDocument && chart.scales && chart.scales.x && chart.scales.y) {
          try {
            chart.update('none'); // Update without animation
            log('FanGraphChartJS', 'Chart updated after graphData change');
          } catch (error) {
            logWarn('FanGraphChartJS', 'Error updating chart after graphData change', error);
          }
        }
      }, 100);
    }
  }, [graphData]);

  // Add direct canvas click handler as fallback (runs after chart is rendered)
  useEffect(() => {
    if (!graphData) return; // Wait for data to load
    
    let cleanup = null;
    
    // Wait for chart to be rendered
    const timeout = setTimeout(() => {
      if (chartRef.current && chartRef.current.canvas && chartRef.current.canvas.ownerDocument) {
        const canvas = chartRef.current.canvas;
        // Additional check for chart scales
        if (chartRef.current.scales && chartRef.current.scales.x && chartRef.current.scales.y) {
          console.log('Setting up canvas click handler', canvas);
          
          const handleCanvasClick = (e) => {
            console.log('CANVAS CLICKED DIRECTLY!', e);
            handleChartClick(e, []);
          };
          
          canvas.addEventListener('click', handleCanvasClick);
          
          // Store cleanup function
          cleanup = () => {
            canvas.removeEventListener('click', handleCanvasClick);
          };
        }
      }
    }, 500); // Give chart time to render
    
    return () => {
      clearTimeout(timeout);
      if (cleanup) cleanup();
    };
  }, [graphData]);

  useEffect(() => {
    log('FanGraphChartJS', 'Component mounted, loading data');
    setLoading(true);
    setError(null);
    
    Promise.all([
      fetch(`${process.env.PUBLIC_URL || ''}/graph_data.json`).then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.json();
      }),
      fetch(`${process.env.PUBLIC_URL || ''}/motor_options_data.json`).then(r => {
        if (!r.ok) {
          logWarn('FanGraphChartJS', 'Motor options file not found, continuing without motor selection');
          return null;
        }
        return r.json();
      }),
      fetch(`${process.env.PUBLIC_URL || ''}/fan_catalog_specs.json`).then(r => {
        if (!r.ok) {
          logWarn('FanGraphChartJS', 'Catalog specs file not found, continuing without catalog info');
          return null;
        }
        return r.json();
      })
    ])
      .then(([graphData, motorData, catalogData]) => {
        log('FanGraphChartJS', 'Data loaded', {
          hasFanCurve: !!graphData.fan_curve,
          hasFanCurves: !!graphData.fan_curves,
          fanCurveLength: graphData.fan_curve?.length || 0,
          fanCurvesLength: graphData.fan_curves?.length || 0,
          operatingPoint: graphData.operating_point,
          motorOptionsCount: motorData?.motor_options?.length || 0,
          hasFanFamilies: !!graphData.fan_families
        });
        
        // Store all families data
        setAllFamiliesData(graphData);
        
        // Check if we have multiple fan families
        if (graphData.fan_families && Object.keys(graphData.fan_families).length > 0) {
          const families = Object.keys(graphData.fan_families).sort(); // Sort alphabetically
          setAvailableFanFamilies(families);
          
          // Extract unique series (910, 911, etc.)
          const seriesSet = new Set();
          families.forEach(familyId => {
            const match = familyId.match(/^(\d+)\./);
            if (match) {
              seriesSet.add(match[1]);
            }
          });
          const series = Array.from(seriesSet).sort();
          setAvailableSeries(series);
          
          // Set default family if not already set
          const defaultFamily = graphData.default_family || families[0];
          if (!selectedFanFamily) {
            setSelectedFanFamily(defaultFamily);
            // Set default series based on default family
            const defaultMatch = defaultFamily.match(/^(\d+)\./);
            if (defaultMatch) {
              setSelectedSeries(defaultMatch[1]);
              // Update available sizes for the default series
              const sizes = families
                .filter(familyId => familyId.startsWith(defaultMatch[1] + '.'))
                .sort();
              setAvailableSizes(sizes);
            }
          } else {
            // Update series based on selected family
            const familyMatch = selectedFanFamily.match(/^(\d+)\./);
            if (familyMatch) {
              setSelectedSeries(familyMatch[1]);
              // Update available sizes for the selected series
              const sizes = families
                .filter(familyId => familyId.startsWith(familyMatch[1] + '.'))
                .sort();
              setAvailableSizes(sizes);
            }
          }
          
          // Load data for selected family
          const familyId = selectedFanFamily || defaultFamily;
          const familyData = graphData.fan_families[familyId];
          
          // Check for either old format (fan_curve/fan_curves) or new format (base_measurements)
          if (!familyData || (!familyData.fan_curve && !familyData.fan_curves && !familyData.base_measurements)) {
            throw new Error(`Invalid data structure for fan family ${familyId}: missing fan curve data or base measurements`);
          }
          
          setGraphData(familyData);
          log('FanGraphChartJS', 'Fan family data loaded', { 
            familyId, 
            curvePoints: familyData.fan_curve?.length || familyData.fan_curves?.length || 0 
          });
        } else {
          // Single family (backward compatibility)
          // Check for either old format (fan_curve/fan_curves) or new format (base_measurements)
          if (!graphData || (!graphData.fan_curve && !graphData.fan_curves && !graphData.base_measurements)) {
            throw new Error('Invalid data structure: missing fan curve data or base measurements');
          }
          setGraphData(graphData);
        }
        
        // Load motor options from the selected family's data (not from separate motor_options_data.json)
        // Motor options are stored per family in graph_data.json
        let targetFamilyId = null;
        if (graphData.fan_families && Object.keys(graphData.fan_families).length > 0) {
          targetFamilyId = selectedFanFamily || (graphData.default_family || Object.keys(graphData.fan_families)[0]);
          
          // Get motor options from the selected family
          if (graphData.fan_families[targetFamilyId] && graphData.fan_families[targetFamilyId].motor_options) {
            setMotorOptions(graphData.fan_families[targetFamilyId].motor_options);
            log('FanGraphChartJS', 'Motor options loaded from family data', { 
              familyId: targetFamilyId,
              count: graphData.fan_families[targetFamilyId].motor_options.length 
            });
          } else {
            // Fallback: try motor_options_data.json if family doesn't have motor options
            if (motorData && motorData.motor_options) {
              setMotorOptions(motorData.motor_options);
              log('FanGraphChartJS', 'Motor options loaded from motor_options_data.json (fallback)', { count: motorData.motor_options.length });
            }
          }
        } else {
          // Single family structure (backward compatibility)
          if (motorData && motorData.motor_options) {
            setMotorOptions(motorData.motor_options);
            log('FanGraphChartJS', 'Motor options loaded from motor_options_data.json', { count: motorData.motor_options.length });
          }
        }
        
        // Handle catalog specs (may be single or multiple families)
        if (catalogData) {
          // Determine which family to use
          let targetFamilyId = null;
          if (graphData.fan_families && Object.keys(graphData.fan_families).length > 0) {
            targetFamilyId = selectedFanFamily || (graphData.default_family || Object.keys(graphData.fan_families)[0]);
          }
          
          if (catalogData.fan_families && targetFamilyId) {
            // Multiple families structure - load specs for selected/default family
            if (catalogData.fan_families[targetFamilyId]) {
              setCatalogSpecs(catalogData.fan_families[targetFamilyId]);
              log('FanGraphChartJS', 'Catalog specs loaded for family', { familyId: targetFamilyId });
            } else {
              logWarn('FanGraphChartJS', 'Catalog specs not found for family', { familyId: targetFamilyId });
            }
          } else if (catalogData.fan_model) {
            // Single family structure (backward compatibility)
            // Only set if it matches the selected family or if we only have one family
            if (!targetFamilyId || catalogData.fan_model === targetFamilyId) {
              setCatalogSpecs(catalogData);
              log('FanGraphChartJS', 'Catalog specs loaded', { fanModel: catalogData.fan_model });
            }
          }
        }
        
        // Calculate FIXED domain once when data loads
        // Generate curve if using new format, otherwise use pre-calculated
        let fanCurve = graphData.fan_curve || (graphData.fan_curves && graphData.fan_curves[0]?.points) || [];
        
        // If new format, generate curve for domain calculation
        if (!fanCurve.length && graphData.base_measurements && graphData.voltage_rpm_mappings) {
          const voltageKeys = Object.keys(graphData.voltage_rpm_mappings);
          if (voltageKeys.length > 0) {
            const targetRPM = graphData.voltage_rpm_mappings[voltageKeys[0]];
            fanCurve = generateFanCurve(graphData.base_measurements, targetRPM);
          } else if (graphData.base_measurements.reference_rpm) {
            fanCurve = generateFanCurve(graphData.base_measurements, graphData.base_measurements.reference_rpm);
          }
        }
        
        if (fanCurve.length > 0) {
          const fanCurveMaxQv = Math.max(...fanCurve.map(d => d.qv), 0);
          const fanCurveMaxPsf = Math.max(...fanCurve.map(d => d.psf), 0);
          const fanCurveMinQv = Math.min(...fanCurve.map(d => d.qv), 0);
          const fanCurveMinPsf = Math.min(...fanCurve.map(d => d.psf), 0);
          
          // Calculate domain to match reference charts - don't start at 0
          // Add padding: 10% above max, but use a reasonable minimum (not 0)
          const fixedMaxQv = Math.ceil(fanCurveMaxQv * 1.1);
          const fixedMaxPsf = Math.ceil(fanCurveMaxPsf * 1.1);
          
          // Set minimum based on data, but ensure reasonable bounds
          // For flow rate: if min is very low, use a reasonable starting point (e.g., 1000 for larger fans)
          // For pressure: if min is very low, use a reasonable starting point (e.g., 200 for larger fans)
          let fixedMinQv = Math.max(0, Math.floor(fanCurveMinQv * 0.9));
          let fixedMinPsf = Math.max(0, Math.floor(fanCurveMinPsf * 0.9));
          
          // For larger fans (maxQv > 5000), use higher minimums to match reference charts
          if (fixedMaxQv > 5000) {
            fixedMinQv = Math.max(fixedMinQv, 1000);  // Match reference: X-axis starts at 1000
          }
          if (fixedMaxPsf > 1500) {
            fixedMinPsf = Math.max(fixedMinPsf, 200);  // Match reference: Y-axis starts at 200
          }
          
          setFixedDomain({
            minQv: fixedMinQv,
            maxQv: fixedMaxQv,
            minPsf: fixedMinPsf,
            maxPsf: fixedMaxPsf
          });
          
          log('FanGraphChartJS', 'Fixed domain calculated', { 
            fixedMaxQv, 
            fixedMaxPsf,
            fixedMinQv,
            fixedMinPsf,
            fanCurveMaxQv,
            fanCurveMaxPsf,
            fanCurveMinQv,
            fanCurveMinPsf,
            note: 'Domain is fixed and will never change, matches reference chart ranges'
          });
        }
        
        if (graphData.operating_point && graphData.operating_point.qv) {
          log('FanGraphChartJS', 'Setting initial operating point', graphData.operating_point);
          setOperatingPoint(graphData.operating_point);
        }
        setLoading(false);
        log('FanGraphChartJS', 'Data loading complete');
      })
      .catch(error => {
        logError('FanGraphChartJS', 'Error loading graph data', { error: error.message, stack: error.stack });
        setError(error.message || 'Failed to load graph data');
        setLoading(false);
      });
  }, []);

  // Prepare fan curve data
  // Support both old format (pre-calculated curves) and new format (base measurements for dynamic generation)
  // NOTE: This hook MUST be called before any early returns to comply with Rules of Hooks
  const fanCurveData = useMemo(() => {
    if (!graphData) return [];
    
    // NEW FORMAT: Generate curve dynamically from base measurements
    if (graphData.base_measurements && graphData.voltage_rpm_mappings) {
      const { base_measurements, voltage_rpm_mappings } = graphData;
      
      // Generate curve for the selected motor's RPM, or use a default voltage
      // Priority: 1) Selected motor RPM, 2) First motor option RPM, 3) Match voltage to motor, 4) Reference RPM
      let targetRPM = null;
      let rpmSource = null;
      
      // Check if we have motor options in graphData (they're loaded with the family data)
      const familyMotorOptions = graphData.motor_options || motorOptions;
      if (familyMotorOptions && familyMotorOptions.length > 0) {
        // Use the first motor option's RPM (typically the default/primary motor)
        const firstMotor = familyMotorOptions[0];
        if (firstMotor && firstMotor.fan_rpm) {
          targetRPM = firstMotor.fan_rpm;
          rpmSource = `motor_${firstMotor.power_rating_kw}kW`;
        }
      }
      
      // Fallback: Try to match a voltage to the motor RPM
      if (!targetRPM && familyMotorOptions && familyMotorOptions.length > 0) {
        const firstMotor = familyMotorOptions[0];
        if (firstMotor && firstMotor.fan_rpm) {
          // Find voltage that matches this RPM (within 50 RPM tolerance)
          const motorRPM = firstMotor.fan_rpm;
          for (const [voltageKey, rpm] of Object.entries(voltage_rpm_mappings)) {
            if (Math.abs(rpm - motorRPM) < 50) {
              targetRPM = rpm;
              rpmSource = `voltage_${voltageKey}_matched_to_motor`;
              break;
            }
          }
        }
      }
      
      // Fallback to first voltage if no motor match
      if (!targetRPM) {
        const voltageKeys = Object.keys(voltage_rpm_mappings);
        if (voltageKeys.length > 0) {
          // Use first voltage's RPM to generate the main fan curve
          const firstVoltage = voltageKeys[0];
          targetRPM = voltage_rpm_mappings[firstVoltage];
          rpmSource = `voltage_${firstVoltage}`;
        }
      }
      
      // Final fallback to reference RPM
      if (!targetRPM && base_measurements.reference_rpm) {
        targetRPM = base_measurements.reference_rpm;
        rpmSource = 'reference_rpm';
      }
      
      if (targetRPM) {
        const generatedCurve = generateFanCurve(base_measurements, targetRPM);
        log('FanGraphChartJS', 'Generated fan curve dynamically', {
          rpmSource,
          rpm: targetRPM,
          points: generatedCurve.length
        });
        return generatedCurve.sort((a, b) => a.qv - b.qv);
      }
    }
    
    // Fallback: Try to use reference RPM if base_measurements exist but no voltage mappings
    if (graphData.base_measurements && graphData.base_measurements.reference_rpm) {
      const generatedCurve = generateFanCurve(graphData.base_measurements, graphData.base_measurements.reference_rpm);
      log('FanGraphChartJS', 'Generated fan curve using reference RPM', {
        rpm: graphData.base_measurements.reference_rpm,
        points: generatedCurve.length
      });
      return generatedCurve.sort((a, b) => a.qv - b.qv);
    }
    
    // OLD FORMAT: Use pre-calculated curves (backward compatibility)
    if (graphData.fan_curve && Array.isArray(graphData.fan_curve) && graphData.fan_curve.length > 0) {
      // Use fan_curve directly (this is the working format from 24h ago)
      const fanCurveMap = new Map();
      graphData.fan_curve.forEach(point => {
        if (point && point.qv !== undefined && point.psf !== undefined) {
          const key = Math.round(point.qv);
          if (!fanCurveMap.has(key) || fanCurveMap.get(key).psf < point.psf) {
            fanCurveMap.set(key, { qv: point.qv, psf: point.psf });
          }
        }
      });
      return Array.from(fanCurveMap.values()).sort((a, b) => a.qv - b.qv);
    } else if (graphData.fan_curves && Array.isArray(graphData.fan_curves) && graphData.fan_curves.length > 0) {
      // Fallback to fan_curves if fan_curve doesn't exist
      // Use the first fan curve (primary curve)
      const primaryCurve = graphData.fan_curves[0];
      if (primaryCurve && primaryCurve.points && Array.isArray(primaryCurve.points)) {
        return primaryCurve.points.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv);
      }
    }
    
    return [];
  }, [graphData, motorOptions]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading graph data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <div>Error: {error}</div>
        <div style={{ marginTop: '10px', fontSize: '14px' }}>
          Please ensure graph_data.json exists in the public folder.
        </div>
      </div>
    );
  }

  if (!graphData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>No graph data available</div>
      </div>
    );
  }

  // Prepare system resistance data
  // If not in JSON, calculate from operating point using psf = k * qv^2
  let systemResistanceData = [];
  if (graphData.system_resistance && Array.isArray(graphData.system_resistance) && graphData.system_resistance.length > 0) {
    systemResistanceData = graphData.system_resistance.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv);
  } else if (operatingPoint.qv > 0 && operatingPoint.psf > 0) {
    // Calculate system resistance curve: psf = k * qv^2
    // k = psf / (qv^2) at operating point
    const k = operatingPoint.psf / (operatingPoint.qv * operatingPoint.qv);
    const maxQv = fixedDomain ? fixedDomain.maxQv : 10000;
    const stepSize = 200;
    
    for (let qv = 0; qv <= maxQv; qv += stepSize) {
      const psf = k * qv * qv;
      systemResistanceData.push({ qv, psf });
    }
  }

  // Prepare ErP range data
  const erpRangeData = graphData.erp_range && Array.isArray(graphData.erp_range)
    ? graphData.erp_range.map(p => ({ qv: p.qv, psf: p.psf })).sort((a, b) => a.qv - b.qv)
    : [];

  // Get domain values
  const maxQv = fixedDomain ? fixedDomain.maxQv : 10000;
  const maxPsf = fixedDomain ? fixedDomain.maxPsf : 1000;
  const minQv = fixedDomain ? fixedDomain.minQv : 0;
  const minPsf = fixedDomain ? fixedDomain.minPsf : 0;

  // Helper function to find pressure at a given qv for any curve
  const findPressureAtQv = (curvePoints, qv) => {
    if (!curvePoints || curvePoints.length === 0) return null;
    
    const minQv = Math.min(...curvePoints.map(p => p.qv));
    const maxQv = Math.max(...curvePoints.map(p => p.qv));
    
    if (qv < minQv) {
      return curvePoints[0].psf;
    } else if (qv > maxQv) {
      return curvePoints[curvePoints.length - 1].psf;
    } else {
      let pointBefore = null;
      let pointAfter = null;
      
      for (let i = 0; i < curvePoints.length - 1; i++) {
        if (curvePoints[i].qv <= qv && curvePoints[i + 1].qv >= qv) {
          pointBefore = curvePoints[i];
          pointAfter = curvePoints[i + 1];
          break;
        }
      }
      
      const exactMatch = curvePoints.find(p => Math.abs(p.qv - qv) < 0.1);
      if (exactMatch) {
        return exactMatch.psf;
      } else if (pointBefore && pointAfter) {
        const ratio = (qv - pointBefore.qv) / (pointAfter.qv - pointBefore.qv);
        return pointBefore.psf + ratio * (pointAfter.psf - pointBefore.psf);
      }
    }
    
    return null;
  };

  // Find closest curve above clicked point (motor or voltage curve)
  const findClosestMotorAbove = (qv, psf) => {
    const candidates = [];
    const allCurves = [];
    
    // Check motor options
    if (motorOptions && motorOptions.length > 0) {
      for (const motor of motorOptions) {
        const curvePoints = motor.curve_points || [];
        if (curvePoints.length === 0) continue;
        
        const pressureAtQv = findPressureAtQv(curvePoints, qv);
        if (pressureAtQv !== null) {
          const pressureDifference = pressureAtQv - psf;
          
          allCurves.push({
            type: 'motor',
            motor,
            pressureAtQv,
            difference: pressureDifference,
            distance: Math.abs(pressureDifference)
          });
          
          if (pressureDifference >= 0) {
            candidates.push({
              type: 'motor',
              motor,
              pressureAtQv,
              difference: pressureDifference,
              distance: Math.abs(pressureDifference)
            });
          }
        }
      }
    }
    
    // Check voltage curves if using new format
    if (graphData && graphData.base_measurements && graphData.voltage_rpm_mappings) {
      const { base_measurements, voltage_rpm_mappings } = graphData;
      
      for (const [voltageKey, rpm] of Object.entries(voltage_rpm_mappings)) {
        const voltageCurve = generateFanCurve(base_measurements, rpm);
        if (voltageCurve && voltageCurve.length > 0) {
          const sortedCurve = voltageCurve.sort((a, b) => a.qv - b.qv);
          const pressureAtQv = findPressureAtQv(sortedCurve, qv);
          
          if (pressureAtQv !== null) {
            const pressureDifference = pressureAtQv - psf;
            
            allCurves.push({
              type: 'voltage',
              voltageKey,
              rpm,
              curvePoints: sortedCurve,
              pressureAtQv,
              difference: pressureDifference,
              distance: Math.abs(pressureDifference)
            });
            
            if (pressureDifference >= 0) {
              candidates.push({
                type: 'voltage',
                voltageKey,
                rpm,
                curvePoints: sortedCurve,
                pressureAtQv,
                difference: pressureDifference,
                distance: Math.abs(pressureDifference)
              });
            }
          }
        }
      }
    }
    
    // Sort candidates: prefer curves above (difference >= 0), then by distance
    if (candidates.length > 0) {
      candidates.sort((a, b) => {
        // First, prefer motors over voltage curves if same distance
        if (Math.abs(a.distance - b.distance) < 1) {
          if (a.type !== b.type) {
            return a.type === 'motor' ? -1 : 1;
          }
        }
        // Then by distance
        return a.distance - b.distance;
      });
      
      const best = candidates[0];
      if (best.type === 'motor') {
        return best.motor;
      } else {
        // For voltage curves, find or create a matching motor option
        // Or return a motor-like object for selection
        const matchingMotor = motorOptions?.find(m => 
          m.fan_rpm && Math.abs(m.fan_rpm - best.rpm) < 50
        );
        if (matchingMotor) {
          return matchingMotor;
        }
        // Create a temporary motor object for voltage curve selection
        return {
          power_rating_kw: parseFloat(best.voltageKey.replace('V', '')),
          fan_rpm: best.rpm,
          curve_points: best.curvePoints,
          isVoltageCurve: true,
          voltageKey: best.voltageKey
        };
      }
    }
    
    // Fallback: find closest curve even if below
    if (allCurves.length > 0) {
      allCurves.sort((a, b) => {
        if (Math.abs(a.distance - b.distance) > 1) {
          return a.distance - b.distance;
        }
        return a.type === 'motor' ? -1 : 1;
      });
      
      const best = allCurves[0];
      if (best.type === 'motor') {
        return best.motor;
      } else {
        const matchingMotor = motorOptions?.find(m => 
          m.fan_rpm && Math.abs(m.fan_rpm - best.rpm) < 50
        );
        if (matchingMotor) {
          return matchingMotor;
        }
        return {
          power_rating_kw: parseFloat(best.voltageKey.replace('V', '')),
          fan_rpm: best.rpm,
          curve_points: best.curvePoints,
          isVoltageCurve: true,
          voltageKey: best.voltageKey
        };
      }
    }
    
    return null;
  };

  // Handle click on chart using Chart.js built-in coordinate conversion
  const handleChartClick = (event, elements) => {
    console.log('CHART CLICKED!', { event, elements, hasChartRef: !!chartRef.current });
    
    if (!chartRef.current) {
      logWarn('ClickHandler', 'Chart ref not available');
      return;
    }

    const chart = chartRef.current;
    
    // Chart.js onClick provides the native event - handle both React synthetic and native events
    let nativeEvent;
    if (event.nativeEvent) {
      nativeEvent = event.nativeEvent;
    } else if (event.clientX !== undefined) {
      nativeEvent = event;
    } else {
      logWarn('ClickHandler', 'Could not extract native event from', event);
      return;
    }
    
    // Get the canvas element from the chart
    const canvas = chart.canvas;
    if (!canvas) {
      logWarn('ClickHandler', 'Canvas not found');
      return;
    }
    
    const rect = canvas.getBoundingClientRect();
    
    // Calculate click position relative to canvas
    const canvasPosition = {
      x: nativeEvent.clientX - rect.left,
      y: nativeEvent.clientY - rect.top
    };

    console.log('Canvas position calculated', { 
      canvasPosition, 
      rect, 
      clientX: nativeEvent.clientX, 
      clientY: nativeEvent.clientY,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    // Use Chart.js built-in methods to convert pixel to data coordinates
    const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);
    const dataY = chart.scales.y.getValueForPixel(canvasPosition.y);
    
    console.log('Data coordinates calculated', { dataX, dataY, canvasPosition });

    log('ClickHandler', 'Chart.js coordinate conversion', {
      canvasX: canvasPosition.x,
      canvasY: canvasPosition.y,
      dataX: dataX.toFixed(2),
      dataY: dataY.toFixed(2),
      algorithm: 'Chart.js getValueForPixel (built-in)'
    });

    // Record click in history
    const clickRecord = {
      timestamp: new Date().toISOString(),
      pixelX: canvasPosition.x,
      pixelY: canvasPosition.y,
      algorithm: 'Chart.js getValueForPixel',
      calculatedData: { qv: parseFloat(dataX.toFixed(2)), psf: parseFloat(dataY.toFixed(2)) }
    };
    
    clickHistoryRef.current.push(clickRecord);
    
    log('ClickHandler', '=== CLICK COORDINATE HISTORY TABLE ===', {
      totalClicks: clickHistoryRef.current.length,
      clickHistory: clickHistoryRef.current.map((record, idx) => ({
        '#': idx + 1,
        'Pixel (X,Y)': `(${record.pixelX.toFixed(1)}, ${record.pixelY.toFixed(1)})`,
        'Algorithm': record.algorithm,
        'Calculated Data (qv,psf)': `(${record.calculatedData.qv}, ${record.calculatedData.psf})`
      }))
    });

    // Clamp to valid ranges
    const clampedQv = Math.max(minQv, Math.min(maxQv, dataX));
    const clampedPsf = Math.max(minPsf, Math.min(maxPsf, dataY));

    log('ClickHandler', 'Setting operating point from click', {
      clickedQv: dataX.toFixed(2),
      clickedPsf: dataY.toFixed(2),
      clampedQv: clampedQv.toFixed(2),
      clampedPsf: clampedPsf.toFixed(2)
    });

    // Set operating point with the clicked values (this will update the text boxes)
    // Use the exact clicked values, not clamped (unless they're out of bounds)
    setOperatingPoint({
      qv: clampedQv,
      psf: clampedPsf
    });
    
    log('ClickHandler', 'Operating point state updated', {
      qv: clampedQv,
      psf: clampedPsf
    });

    // Find closest motor above this point
    const foundMotor = findClosestMotorAbove(clampedQv, clampedPsf);
    log('ClickHandler', 'Motor selection result', {
      foundMotor: foundMotor ? {
        power: foundMotor.power_rating_kw,
        rpm: foundMotor.fan_rpm,
        poles: foundMotor.poles
      } : null,
      clickedQv: clampedQv,
      clickedPsf: clampedPsf,
      motorOptionsCount: motorOptions.length
    });
    
    if (foundMotor) {
      setSelectedMotor(foundMotor);
      log('ClickHandler', 'Motor selected and state updated', {
        power: foundMotor.power_rating_kw,
        rpm: foundMotor.fan_rpm,
        poles: foundMotor.poles
      });
    } else {
      setSelectedMotor(null);
      logWarn('ClickHandler', 'No motor found for clicked point');
    }

    flushLogs();
  };

  // Prepare datasets for Chart.js
  const datasets = [];

  // ErP Range (filled area)
  if (erpRangeData.length > 0) {
    datasets.push({
      label: 'ErP konformer Bereich',
      data: erpRangeData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: 'transparent',
      backgroundColor: 'rgba(135, 206, 235, 0.3)',
      fill: true,
      pointRadius: 0,
      borderWidth: 0,
      order: 0
    });
  }

  // Main Fan Curve - Darker gray dashed lines
  if (fanCurveData.length > 0) {
    datasets.push({
      label: 'Fan Curve',
      data: fanCurveData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: 'rgba(85, 85, 85, 0.8)', // Darker gray, 80% opacity
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [4, 4],
      pointRadius: 0,
      fill: false,
      order: 1
    });
  }

  // System Resistance Curve - Blue
  if (systemResistanceData.length > 0) {
    datasets.push({
      label: 'System Resistance',
      data: systemResistanceData.map(p => ({ x: p.qv, y: p.psf })),
      borderColor: '#0066cc',
      backgroundColor: 'transparent',
      borderWidth: 3,
      borderDash: [5, 5],
      pointRadius: 0,
      fill: false,
      order: 2
    });
  }

  // Motor Option Curves - Highlight when selected
  log('DatasetCreation', 'Creating motor curves', {
    motorOptionsCount: motorOptions.length,
    selectedMotor: selectedMotor ? {
      power: selectedMotor.power_rating_kw,
      rpm: selectedMotor.fan_rpm
    } : null
  });
  
  motorOptions.forEach((motor) => {
    // Check if this motor is selected (by power rating or by RPM if it's a voltage curve match)
    const isSelected = selectedMotor && (
      selectedMotor.power_rating_kw === motor.power_rating_kw ||
      (selectedMotor.fan_rpm && motor.fan_rpm && Math.abs(selectedMotor.fan_rpm - motor.fan_rpm) < 50)
    );
    
    // Generate curve dynamically if base_measurements available, otherwise use pre-calculated
    let curvePoints = motor.curve_points || [];
    
    if (graphData.base_measurements && motor.fan_rpm) {
      // NEW FORMAT: Generate curve dynamically using fan laws
      const generatedCurve = generateFanCurve(graphData.base_measurements, motor.fan_rpm);
      if (generatedCurve.length > 0) {
        curvePoints = generatedCurve;
        log('FanGraphChartJS', `Generated motor curve dynamically for ${motor.power_rating_kw}kW`, {
          rpm: motor.fan_rpm,
          points: curvePoints.length
        });
      }
    }
    
    if (curvePoints.length > 0) {
      log('DatasetCreation', `Motor ${motor.power_rating_kw}kW`, {
        isSelected,
        curvePointsCount: curvePoints.length,
        borderColor: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.6)',
        borderWidth: isSelected ? 3 : 1
      });
      // Find indices for left (first point) and right (last point) label placement
      const leftIndex = 0; // First point (left side)
      const rightIndex = curvePoints.length - 1; // Last point (right side)
      
      datasets.push({
        label: `${motor.power_rating_kw}kW (${motor.fan_rpm.toFixed(0)} RPM)`,
        data: curvePoints.map(p => ({ x: p.qv, y: p.psf })),
        borderColor: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.6)', // Orange when selected, purple 60% opacity when not
        backgroundColor: 'transparent',
        borderWidth: isSelected ? 3 : 1, // Bold (3px) when selected, thin (1px) when not
        borderDash: isSelected ? [] : [2, 2], // Solid when selected, dashed when not
        pointRadius: 0,
        fill: false,
        order: isSelected ? 4 : 3, // Selected motor on top
        // Add datalabels configuration for this dataset
        datalabels: {
          display: (context) => {
            // Show labels at both left (first) and right (last) points
            return context.dataIndex === leftIndex || context.dataIndex === rightIndex;
          },
          anchor: (context) => {
            // Left side: anchor to start, align left
            // Right side: anchor to end, align right
            return context.dataIndex === leftIndex ? 'start' : 'end';
          },
          align: (context) => {
            // Left side: align to the right of the point
            // Right side: align to the left of the point
            return context.dataIndex === leftIndex ? 'right' : 'left';
          },
          offset: (context) => {
            // Left side: offset to the left
            // Right side: offset to the right
            return context.dataIndex === leftIndex ? -5 : 5;
          },
          color: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.8)',
          font: {
            weight: 'bold',
            size: 11
          },
          formatter: () => {
            return `${motor.fan_rpm.toFixed(0)}`;
          }
        }
      });
    }
  });

  // Voltage Curves - Generate from voltage-RPM mappings if using new format
  // Also generate curves from motor options if they have fan_rpm values
  if (graphData.base_measurements) {
    const { base_measurements } = graphData;
    
    // First, generate curves from motor options (if available) - these are the actual motor specs
    const motorRPMs = new Set();
    if (motorOptions && motorOptions.length > 0) {
      motorOptions.forEach(motor => {
        if (motor.fan_rpm && typeof motor.fan_rpm === 'number' && motor.fan_rpm > 0) {
          motorRPMs.add(motor.fan_rpm);
        }
      });
    }
    
    // Then, generate curves from voltage-RPM mappings (for variable-speed operation)
    if (graphData.voltage_rpm_mappings) {
      const { voltage_rpm_mappings } = graphData;
      Object.entries(voltage_rpm_mappings).forEach(([voltageKey, rpm]) => {
        // Only add if not already covered by motor options
        if (!motorRPMs.has(rpm)) {
          motorRPMs.add(rpm);
        }
      });
    }
    
    // Generate a curve for each unique RPM
    Array.from(motorRPMs).sort((a, b) => a - b).forEach(rpm => {
      const voltageCurve = generateFanCurve(base_measurements, rpm);
      if (voltageCurve && voltageCurve.length > 0) {
        // Find left and right indices for RPM labels
        const sortedCurve = voltageCurve.sort((a, b) => a.qv - b.qv);
        const leftIndex = 0;
        const rightIndex = sortedCurve.length - 1;
        
        // Check if this curve is selected (by RPM match)
        const isSelected = selectedMotor && 
          selectedMotor.fan_rpm && 
          Math.abs(selectedMotor.fan_rpm - rpm) < 50;
        
        // Find matching motor option or voltage for label
        let label = `${rpm.toFixed(0)} RPM`;
        if (motorOptions && motorOptions.length > 0) {
          const matchingMotor = motorOptions.find(m => m.fan_rpm && Math.abs(m.fan_rpm - rpm) < 10);
          if (matchingMotor && matchingMotor.power_rating_kw) {
            label = `${matchingMotor.power_rating_kw}kW (${rpm.toFixed(0)} RPM)`;
          }
        }
        if (graphData.voltage_rpm_mappings) {
          const matchingVoltage = Object.entries(graphData.voltage_rpm_mappings).find(([v, r]) => Math.abs(r - rpm) < 10);
          if (matchingVoltage && !label.includes('kW')) {
            label = `${matchingVoltage[0]} (${rpm.toFixed(0)} RPM)`;
          }
        }
        
        datasets.push({
          label: label,
          data: sortedCurve.map(p => ({ x: p.qv, y: p.psf })),
          borderColor: isSelected ? '#ff6600' : '#8884d8', // Orange when selected, purple otherwise
          backgroundColor: 'transparent',
          borderWidth: isSelected ? 3 : 1, // Bold when selected
          borderDash: isSelected ? [] : [2, 2], // Solid when selected, dashed otherwise
          pointRadius: 0,
          fill: false,
          order: isSelected ? 4 : 2, // Selected on top
          // Add RPM labels at left and right
          datalabels: {
            display: (context) => {
              return context.dataIndex === leftIndex || context.dataIndex === rightIndex;
            },
            anchor: (context) => {
              return context.dataIndex === leftIndex ? 'start' : 'end';
            },
            align: (context) => {
              return context.dataIndex === leftIndex ? 'right' : 'left';
            },
            offset: (context) => {
              return context.dataIndex === leftIndex ? -5 : 5;
            },
            color: isSelected ? '#ff6600' : 'rgba(136, 132, 216, 0.8)',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: () => {
              return `${rpm.toFixed(0)}`;
            }
          }
        });
      }
    });
  } else if (graphData.voltage_curves) {
    // OLD FORMAT: Use pre-calculated voltage curves
    Object.keys(graphData.voltage_curves).forEach(voltage => {
      const voltageData = graphData.voltage_curves[voltage];
      if (voltageData && Array.isArray(voltageData) && voltageData.length > 0) {
        datasets.push({
          label: `${voltage}V`,
          data: voltageData.map(p => ({ x: p.qv, y: p.psf })),
          borderColor: '#8884d8', // Purple like Recharts
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderDash: [2, 2], // Dashed like Recharts
          pointRadius: 0,
          fill: false,
          order: 2
        });
      }
    });
  }

  // Operating Point Vertical Reference Line - Light gray crosshair
  datasets.push({
    label: 'Operating Point (X)',
    data: [
      { x: operatingPoint.qv, y: minPsf },
      { x: operatingPoint.qv, y: maxPsf }
    ],
    borderColor: '#d0d0d0',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderDash: [3, 3],
    pointRadius: 0,
    fill: false,
    order: 9,
    pointHoverRadius: 0,
    pointHoverBorderWidth: 0
  });

  // Operating Point Horizontal Reference Line - Light gray crosshair
  datasets.push({
    label: 'Operating Point (Y)',
    data: [
      { x: minQv, y: operatingPoint.psf },
      { x: maxQv, y: operatingPoint.psf }
    ],
    borderColor: '#d0d0d0',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderDash: [3, 3],
    pointRadius: 0,
    fill: false,
    order: 9,
    pointHoverRadius: 0,
    pointHoverBorderWidth: 0
  });

  // Operating Point Marker
  datasets.push({
    label: 'Operating Point',
    data: [{ x: operatingPoint.qv, y: operatingPoint.psf }],
    borderColor: '#003366',
    backgroundColor: '#003366',
    borderWidth: 2,
    pointRadius: 8, // Keep the operating point marker visible
    pointHoverRadius: 10,
    pointBackgroundColor: '#003366',
    pointBorderColor: '#ffffff',
    pointBorderWidth: 2,
    showLine: false,
    order: 10
  });

  // Chart configuration
  const chartData = {
    datasets: datasets
  };
  
  // Log dataset creation for debugging
  log('DatasetCreation', 'Final datasets created', {
    totalDatasets: datasets.length,
    motorDatasets: datasets.filter(d => d.label && d.label.includes('kW')).length,
    selectedMotorInDatasets: datasets.some(d => d.borderColor === '#ff6600'),
    selectedMotorState: selectedMotor ? selectedMotor.power_rating_kw : null
  });
  
  // Log dataset creation for debugging
  log('DatasetCreation', 'Final datasets created', {
    totalDatasets: datasets.length,
    motorDatasets: datasets.filter(d => d.label.includes('kW')).length,
    selectedMotorInDatasets: datasets.some(d => d.borderColor === '#ff6600')
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false, // Disable all animations
    transitions: {
      active: {
        animation: {
          duration: 0 // No animation on state changes
        }
      }
    },
    elements: {
      point: {
        radius: 0, // Hide all points by default
        hoverRadius: 0
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        display: true,
        filter: (legendItem) => {
          // Hide reference lines from legend
          return !legendItem.text.includes('Operating Point (X)') && 
                 !legendItem.text.includes('Operating Point (Y)');
        }
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: false // Disable tooltip completely
      },
      datalabels: {
        // Global datalabels configuration (can be overridden per dataset)
        display: false, // Default to false, enable per dataset
        color: '#333',
        font: {
          size: 11,
          weight: 'bold'
        },
        padding: {
          top: 4,
          bottom: 4,
          left: 4,
          right: 4
        },
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderColor: '#ccc',
        borderRadius: 3,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Volumenstrom qv [m³/h]'
        },
        min: minQv,
        max: maxQv,
        grid: {
          color: '#fefefe', // Very light gray grid
          borderDash: [3, 3]
        },
        ticks: {
          stepSize: 200,
          maxTicksLimit: Math.ceil((maxQv - minQv) / 200),
          callback: function(value) {
            // Round to nearest 100 and return as whole number (no decimals)
            const rounded = Math.round(value / 100) * 100;
            return rounded.toString();
          }
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: 'Statischer Ventilatordruck P sf [Pa]'
        },
        min: minPsf,
        max: maxPsf,
        grid: {
          color: '#fefefe', // Very light gray grid
          borderDash: [3, 3]
        },
        ticks: {
          stepSize: 200,
          maxTicksLimit: Math.ceil((maxPsf - minPsf) / 200),
          callback: function(value) {
            // Round to nearest 100 and return as whole number (no decimals)
            const rounded = Math.round(value / 100) * 100;
            return rounded.toString();
          }
        }
      }
    },
    onClick: handleChartClick
  };

  // Handle series change
  const handleSeriesChange = (series) => {
    setSelectedSeries(series);
    setSelectedFanFamily(null); // Clear family selection when changing series
    setSelectedMotor(null);
    setCatalogSpecs(null);
    
    // Update available sizes for the selected series
    if (allFamiliesData && allFamiliesData.fan_families) {
      const sizes = Object.keys(allFamiliesData.fan_families)
        .filter(familyId => familyId.startsWith(series + '.'))
        .sort();
      setAvailableSizes(sizes);
      
      // Auto-select first size if available
      if (sizes.length > 0) {
        handleFanFamilyChange(sizes[0]);
      }
    }
  };
  
  // Handle size/type change
  const handleSizeChange = (familyId) => {
    handleFanFamilyChange(familyId);
  };
  
  // Handle fan family change
  const handleFanFamilyChange = (familyId) => {
    setSelectedFanFamily(familyId);
    setSelectedMotor(null); // Reset motor selection when changing families
    setCatalogSpecs(null); // Clear catalog specs - they're family-specific
    
    // Update series based on selected family
    const familyMatch = familyId.match(/^(\d+)\./);
    if (familyMatch) {
      setSelectedSeries(familyMatch[1]);
    }
    
      // Use already loaded data if available
      if (allFamiliesData && allFamiliesData.fan_families && allFamiliesData.fan_families[familyId]) {
        const familyData = allFamiliesData.fan_families[familyId];
        setGraphData(familyData);
        
        // Update motor options for the selected family
        if (familyData.motor_options) {
          setMotorOptions(familyData.motor_options);
          log('FanGraphChartJS', 'Motor options updated for family', { 
            familyId, 
            count: familyData.motor_options.length 
          });
        } else {
          setMotorOptions([]);
          log('FanGraphChartJS', 'No motor options found for family', { familyId });
        }
        
        // Recalculate fixed domain for the new family
        // Generate curve if using new format, otherwise use pre-calculated
        let fanCurve = familyData.fan_curve || (familyData.fan_curves && familyData.fan_curves[0]?.points) || [];
        
        // If new format, generate curve for domain calculation
        if (!fanCurve.length && familyData.base_measurements && familyData.voltage_rpm_mappings) {
          const voltageKeys = Object.keys(familyData.voltage_rpm_mappings);
          if (voltageKeys.length > 0) {
            const targetRPM = familyData.voltage_rpm_mappings[voltageKeys[0]];
            fanCurve = generateFanCurve(familyData.base_measurements, targetRPM);
          } else if (familyData.base_measurements.reference_rpm) {
            fanCurve = generateFanCurve(familyData.base_measurements, familyData.base_measurements.reference_rpm);
          }
        }
        
        if (fanCurve.length > 0) {
          const fanCurveMaxQv = Math.max(...fanCurve.map(d => d.qv), 0);
          const fanCurveMaxPsf = Math.max(...fanCurve.map(d => d.psf), 0);
          const fanCurveMinQv = Math.min(...fanCurve.map(d => d.qv), 0);
          const fanCurveMinPsf = Math.min(...fanCurve.map(d => d.psf), 0);
          
          // Calculate domain to match reference charts - don't start at 0
          const fixedMaxQv = Math.ceil(fanCurveMaxQv * 1.1);
          const fixedMaxPsf = Math.ceil(fanCurveMaxPsf * 1.1);
          
          // Set minimum based on data, but ensure reasonable bounds
          let fixedMinQv = Math.max(0, Math.floor(fanCurveMinQv * 0.9));
          let fixedMinPsf = Math.max(0, Math.floor(fanCurveMinPsf * 0.9));
          
          // For larger fans (maxQv > 5000), use higher minimums to match reference charts
          if (fixedMaxQv > 5000) {
            fixedMinQv = Math.max(fixedMinQv, 1000);  // Match reference: X-axis starts at 1000
          }
          if (fixedMaxPsf > 1500) {
            fixedMinPsf = Math.max(fixedMinPsf, 200);  // Match reference: Y-axis starts at 200
          }
          
          setFixedDomain({
            minQv: fixedMinQv,
            maxQv: fixedMaxQv,
            minPsf: fixedMinPsf,
            maxPsf: fixedMaxPsf
          });
        }
      
      // Try to load catalog specs for this family
      fetch(`${process.env.PUBLIC_URL || ''}/fan_catalog_specs.json`)
        .then(r => {
          if (!r.ok) {
            logWarn('FanGraphChartJS', 'Catalog specs file not found for family', { familyId });
            return null;
          }
          return r.json();
        })
        .then(catalogData => {
          if (catalogData) {
            // Check if it's multi-family structure
            if (catalogData.fan_families && catalogData.fan_families[familyId]) {
              setCatalogSpecs(catalogData.fan_families[familyId]);
              log('FanGraphChartJS', 'Catalog specs loaded for family', { familyId });
            } else if (catalogData.fan_model === familyId) {
              // Single family structure (backward compatibility)
              setCatalogSpecs(catalogData);
              log('FanGraphChartJS', 'Catalog specs loaded for family', { familyId });
            } else {
              logWarn('FanGraphChartJS', 'Catalog specs do not match selected family', { 
                selected: familyId, 
                found: catalogData.fan_model 
              });
            }
          }
        })
        .catch(err => {
          logWarn('FanGraphChartJS', 'Error loading catalog specs', err);
        });
      
      log('FanGraphChartJS', 'Fan family changed', { familyId });
    } else {
      // Reload if data not available
      setLoading(true);
      fetch(`${process.env.PUBLIC_URL || ''}/graph_data.json`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
          return r.json();
        })
        .then(allData => {
          setAllFamiliesData(allData);
          if (allData.fan_families && allData.fan_families[familyId]) {
            const familyData = allData.fan_families[familyId];
            setGraphData(familyData);
            
            // Recalculate fixed domain
            // Generate curve if using new format, otherwise use pre-calculated
            let fanCurve = familyData.fan_curve || (familyData.fan_curves && familyData.fan_curves[0]?.points) || [];
            
            // If new format, generate curve for domain calculation
            if (!fanCurve.length && familyData.base_measurements && familyData.voltage_rpm_mappings) {
              const voltageKeys = Object.keys(familyData.voltage_rpm_mappings);
              if (voltageKeys.length > 0) {
                const targetRPM = familyData.voltage_rpm_mappings[voltageKeys[0]];
                fanCurve = generateFanCurve(familyData.base_measurements, targetRPM);
              } else if (familyData.base_measurements.reference_rpm) {
                fanCurve = generateFanCurve(familyData.base_measurements, familyData.base_measurements.reference_rpm);
              }
            }
            
            if (fanCurve.length > 0) {
              const fanCurveMaxQv = Math.max(...fanCurve.map(d => d.qv), 0);
              const fanCurveMaxPsf = Math.max(...fanCurve.map(d => d.psf), 0);
              
              const fixedMaxQv = Math.ceil(fanCurveMaxQv * 1.1);
              const fixedMaxPsf = Math.ceil(fanCurveMaxPsf * 1.1);
              
              setFixedDomain({
                minQv: 0,
                maxQv: fixedMaxQv,
                minPsf: 0,
                maxPsf: fixedMaxPsf
              });
            }
            
            setLoading(false);
            log('FanGraphChartJS', 'Fan family changed', { familyId });
          } else {
            throw new Error(`Fan family ${familyId} not found`);
          }
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
          logError('FanGraphChartJS', 'Error loading fan family', err);
        });
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      padding: '20px', 
      gap: '20px', 
      fontFamily: 'Arial, sans-serif',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      overflowY: 'visible',
      margin: 0
    }}>
      {/* Fan Family Selector - Two Level Selection */}
      {availableFanFamilies.length > 1 && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f0f0f0',
          borderRadius: '5px',
          border: '1px solid #ddd',
          marginBottom: '10px'
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Fan Family:
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Series Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Series:</label>
              <select
                value={selectedSeries || ''}
                onChange={(e) => handleSeriesChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  minWidth: '100px'
                }}
              >
                <option value="">Select Series</option>
                {availableSeries.map(series => (
                  <option key={series} value={series}>
                    {series}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Size/Type Selector */}
            {selectedSeries && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Size/Type:</label>
                <select
                  value={selectedFanFamily || ''}
                  onChange={(e) => handleSizeChange(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '200px'
                  }}
                >
                  <option value="">Select Size/Type</option>
                  {availableSizes
                    .filter(familyId => familyId.startsWith(selectedSeries + '.'))
                    .map(familyId => {
                      // Extract size and type from familyId (e.g., "911.250FSVS" -> "250 FSVS")
                      const match = familyId.match(/^\d+\.(\d+)([A-Z]+)/);
                      const displayName = match ? `${match[1]} ${match[2]}` : familyId;
                      return (
                        <option key={familyId} value={familyId}>
                          {displayName}
                        </option>
                      );
                    })}
                </select>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Fan Image and Specifications Section */}
      {catalogSpecs && (
        <div style={{
          display: 'flex',
          gap: '20px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '5px',
          border: '1px solid #ddd',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Fan Images Gallery */}
          <div style={{ flexShrink: 0 }}>
            {catalogSpecs.images && catalogSpecs.images.length > 0 ? (
              <div>
                <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                  Fan Images
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '10px',
                  maxWidth: '600px'
                }}>
                  {catalogSpecs.images.map((imgName, idx) => (
                    <div key={idx} style={{
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      padding: '5px',
                      backgroundColor: 'white',
                      textAlign: 'center'
                    }}>
                      <img 
                        src={`${process.env.PUBLIC_URL || ''}/images/${imgName}`}
                        alt={`${catalogSpecs.fan_model} - Image ${idx + 1}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextSibling;
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      />
                      <div style={{ 
                        display: 'none', 
                        fontSize: '11px', 
                        color: '#999',
                        padding: '10px'
                      }}>
                        Image not found
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{
                width: '300px',
                height: '300px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}>
                No images available
              </div>
            )}
          </div>
          
          {/* Fan Specifications */}
          <div style={{ flex: '1' }}>
            <h2 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '20px' }}>
              {catalogSpecs.title}
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                <strong>Typ:</strong> {catalogSpecs.fan_type}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                <strong>Größe:</strong> {catalogSpecs.fan_size}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                <strong>Modell:</strong> {catalogSpecs.fan_model}
              </div>
            </div>
            
            {/* Motor Options Table */}
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                Verfügbare Motoroptionen
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  backgroundColor: 'white'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Leistung (kW)</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Motor-Typ</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Motor RPM</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Ventilator RPM</th>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ccc' }}>Zubehör</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...catalogSpecs.motor_options]
                      .sort((a, b) => {
                        // Sort alphabetically by motor_type (requirement: table must be alphabetical)
                        const typeA = (a.motor_type || '').toLowerCase();
                        const typeB = (b.motor_type || '').toLowerCase();
                        if (typeA !== typeB) {
                          return typeA.localeCompare(typeB);
                        }
                        // If motor_type is the same, sort by power_kw as secondary sort
                        return (a.power_kw || 0) - (b.power_kw || 0);
                      })
                      .map((motor, index) => (
                      <tr key={index} style={{
                        backgroundColor: selectedMotor && selectedMotor.power_rating_kw === motor.power_kw ? '#fff4e6' : 'white'
                      }}>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.power_kw}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.motor_type || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.motor_rpm || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc' }}>{motor.fan_rpm || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ccc', fontSize: '11px' }}>
                          {motor.accessories || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content Area */}
      <div style={{ 
        display: 'flex', 
        gap: '20px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
      {/* Graph Area */}
      <div style={{ flex: '1', minWidth: '700px', flexShrink: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>
            {selectedFanFamily || graphData?.fan_model || 'Fan Performance Graph'}
          </h2>
          <button
            onClick={downloadLogs}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            title="Download debug logs"
          >
            Download Logs
          </button>
        </div>
        <div 
          style={{ 
            cursor: 'crosshair', 
            position: 'relative',
            height: '600px'
          }}
          title="Click on the graph to set a new operating point"
        >
          <Line
            ref={chartRef}
            data={chartData}
            options={chartOptions}
            key="fan-graph-chart" // Stable key to prevent unnecessary re-renders
            updateMode="none" // Don't auto-update, we'll manually update when needed
            onClick={(event, elements) => {
              console.log('Line component onClick triggered', { event, elements });
              handleChartClick(event, elements);
            }}
          />
        </div>
        <div style={{ marginTop: '5px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
          Click anywhere on the graph to set a new operating point
        </div>
        
        {/* Operating Point Labels */}
        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
          <div style={{ backgroundColor: '#003366', color: 'white', padding: '5px 10px', borderRadius: '3px' }}>
            qv: {operatingPoint.qv.toFixed(2)} m³/h
          </div>
          <div style={{ backgroundColor: '#003366', color: 'white', padding: '5px 10px', borderRadius: '3px' }}>
            psf: {operatingPoint.psf.toFixed(2)} Pa
          </div>
        </div>
        
        {/* Selected Motor Display */}
        {selectedMotor && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#e8f4f8', 
            border: '2px solid #0066cc',
            borderRadius: '5px'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#0066cc', fontSize: '16px' }}>
              Empfohlener Motor
            </h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div><strong>Leistung:</strong> {selectedMotor.power_rating_kw} kW</div>
              <div><strong>Ventilator-Drehzahl:</strong> {selectedMotor.fan_rpm.toFixed(0)} RPM</div>
              <div><strong>Motor-Drehzahl:</strong> {selectedMotor.motor_rpm?.toFixed(0) || 'N/A'} RPM</div>
              <div><strong>Motor-Typ:</strong> {selectedMotor.motor_type || 'N/A'}</div>
              <div><strong>Pole:</strong> {selectedMotor.poles}</div>
            </div>
          </div>
        )}
      </div>

      {/* Control Panel */}
      <div style={{ 
        width: '300px', 
        minWidth: '300px',
        flexShrink: 0,
        backgroundColor: '#f9f9f9', 
        padding: '20px', 
        borderRadius: '5px',
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>BETRIEBSPUNKT ÄNDERN</h3>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Volumenstrom (m³/h):</label>
            <input
              type="number"
              value={operatingPoint.qv.toFixed(2)}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setOperatingPoint({ ...operatingPoint, qv: val });
                }
              }}
              style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ccc', borderRadius: '3px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', fontStyle: 'italic' }}>
              Clicked: {operatingPoint.qv.toFixed(2)} m³/h
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Druck (Pa):</label>
            <input
              type="number"
              value={operatingPoint.psf.toFixed(2)}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 0) {
                  setOperatingPoint({ ...operatingPoint, psf: val });
                }
              }}
              style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ccc', borderRadius: '3px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', fontStyle: 'italic' }}>
              Clicked: {operatingPoint.psf.toFixed(2)} Pa
            </div>
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>ZUSÄTZLICHE KURVE EINBLENDEN</h3>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              power: !showAdditionalCurves.power
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.power ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.power ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Elektrische Eingangsleistung
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              current: !showAdditionalCurves.current
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.current ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.current ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Stromaufnahme
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              staticEfficiency: !showAdditionalCurves.staticEfficiency
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.staticEfficiency ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.staticEfficiency ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Statischer Ventilatorwirkungsgrad
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              totalEfficiency: !showAdditionalCurves.totalEfficiency
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.totalEfficiency ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.totalEfficiency ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Totaler Ventilatorwirkungsgrad
          </button>
          <button
            onClick={() => setShowAdditionalCurves({
              ...showAdditionalCurves,
              speed: !showAdditionalCurves.speed
            })}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '5px',
              backgroundColor: showAdditionalCurves.speed ? '#4CAF50' : '#f0f0f0',
              color: showAdditionalCurves.speed ? 'white' : '#333',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
          >
            Drehzahl
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default FanGraphChartJS;

